import { DownloadOutlined } from "@ant-design/icons"
import { Button, Modal, Space } from "antd"
import { useState } from "react"

import { downloadFile } from "../../utils/download-file.ts"
import { FilePreviewContent } from "./FilePreviewContent.tsx"

export { getFileType, resolvePreviewType } from "./FilePreviewContent.tsx"

interface FileDoc {
  file: string
  file_name?: string
  name?: string
  original_filename?: string
  response?: { original_filename?: string }
  version?: number
}

const pickNonEmpty = (v: string | undefined | null): string | undefined => {
  const s = v?.trim()
  return s ? s : undefined
}

const filenameFromUrl = (url: string): string | undefined => {
  if (!url) return undefined
  try {
    const path = new URL(url).pathname
    const segment = path.split("/").filter(Boolean).pop()
    if (!segment) return undefined
    return decodeURIComponent(segment)
  } catch {
    return undefined
  }
}

const resolveDisplayFileName = (doc: FileDoc | null): string => {
  if (!doc) return "File"
  const candidates = [
    pickNonEmpty(doc.file_name),
    pickNonEmpty(doc.name),
    pickNonEmpty(doc.original_filename),
    pickNonEmpty(doc.response?.original_filename)
  ]
  for (const c of candidates) {
    if (c) return c
  }
  const fromUrl = filenameFromUrl(doc.file)
  if (fromUrl) return fromUrl
  return "File"
}

interface FilePreviewModalProps {
  visible: boolean
  onClose: () => void
  doc: FileDoc | null
}

export const FilePreviewModal = ({
  visible,
  onClose,
  doc
}: FilePreviewModalProps) => {
  const name = resolveDisplayFileName(doc)
  const fileUrl = doc?.file || ""

  const handleDownload = async () => {
    if (!fileUrl) return
    await downloadFile(fileUrl, name)
  }

  const title = `Preview: ${name}${doc?.version && doc.version > 1 ? ` (v${doc.version})` : ""}`

  return (
    <Modal
      title={title}
      open={visible}
      onCancel={onClose}
      width="80%"
      style={{ top: 20, maxWidth: 1000 }}
      destroyOnHidden
      footer={
        <Actions onDownloadClick={handleDownload} onCloseClick={onClose} />
      }
      styles={{
        body: {
          padding: "16px 0 0",
          maxHeight: "80vh",
          overflow: "auto"
        }
      }}
    >
      <FilePreviewContent fileUrl={fileUrl} name={name} maxHeight="70vh" />
    </Modal>
  )
}

interface ActionsProps {
  onDownloadClick: () => Promise<void>
  onCloseClick: () => void
}

const Actions = ({ onDownloadClick, onCloseClick }: ActionsProps) => {
  const [downloading, setDownloading] = useState(false)

  const onDownloadClickHandler = async () => {
    try {
      setDownloading(true)
      await onDownloadClick()
    } catch (error) {
      console.error("Failed to download file:", error)
    } finally {
      setDownloading(false)
    }
  }

  return (
    <Space>
      <Button
        loading={downloading}
        icon={<DownloadOutlined />}
        onClick={onDownloadClickHandler}
      >
        Download
      </Button>
      <Button disabled={downloading} onClick={onCloseClick}>
        Close
      </Button>
    </Space>
  )
}

export default FilePreviewModal
